import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@parrot-software/design-system';

// Types
export interface DialogOptions {
  title: string;
  content: ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void | Promise<void>;
}

interface DialogContextType {
  showDialog: (options: DialogOptions) => void;
  hideDialog: () => void;
}

// Context
const DialogContext = createContext<DialogContextType | undefined>(undefined);

// Hook
export const useDialog = (): DialogContextType => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

// HOC
export const withDialog = <P extends object>(Component: React.ComponentType<P>): React.FC<P> => {
  return (props: P) => (
    <DialogProvider>
      <Component {...props} />
    </DialogProvider>
  );
};

// Provider Component
interface DialogProviderProps {
  children: ReactNode;
}

export const DialogProvider = ({ children }: DialogProviderProps): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<DialogOptions | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const showDialog = useCallback((newOptions: DialogOptions) => {
    setOptions(newOptions);
    setIsOpen(true);
  }, []);

  const hideDialog = useCallback(() => {
    setIsOpen(false);
    // Small delay to clear options after animation might be better, but keeping it simple
    setTimeout(() => setOptions(null), 300);
  }, []);

  const handleConfirm = async () => {
    if (options?.onConfirm) {
      try {
        setIsLoading(true);
        await options.onConfirm();
        hideDialog();
      } catch (error) {
        // Handle error silently or integrate with error reporting service
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <DialogContext.Provider value={{ showDialog, hideDialog }}>
      {children}

      {options && (
        <AlertDialog onOpenChange={setIsOpen} open={isOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-start">{options.title}</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="text-sm text-muted-foreground text-start">{options.content}</div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isLoading} onClick={hideDialog}>
                {options.cancelText || 'Cancelar'}
              </AlertDialogCancel>
              {options.onConfirm && (
                <AlertDialogAction
                  className={'bg-destructive text-destructive-foreground hover:bg-destructive/90 self-end'}
                  disabled={isLoading}
                  onClick={(e) => {
                    e.preventDefault();
                    handleConfirm();
                  }}
                >
                  {options.confirmText}
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </DialogContext.Provider>
  );
};
